/**
 * FindMine Cart Integration - "You May Also Like" Widget
 *
 * Displays product recommendations in the cart drawer based on items currently in the cart.
 * Only loads and runs when the cart drawer is open to optimize performance and reduce
 * unnecessary API calls.
 *
 * Features:
 * - Lazy loads only when cart drawer opens
 * - Updates recommendations when cart items change
 * - Falls back to last successful match if no recommendations found
 * - Hides cart drawer slider when FindMine content is available
 *
 * @requires window.theme.apps.findmine.cart_application_id - FindMine cart application ID
 * @requires cart-drawer - Cart drawer custom element
 * @listens cart-state:changed - Fires when cart contents change
 * @listens cart-drawer:opened - Fires when cart drawer opens
 */

if (!window.theme?.apps?.findmine?.cart_application_id) {
  console.warn('FindMine integration: FindMineCartApplicationID is not set.');
}

/**
 * Hides the cart drawer slider when FindMine content is displayed
 *
 * This prevents duplicate content showing when FindMine recommendations are available.
 */
const hideCartDrawerSlider = () => {
  const cartDrawerSlider = document.querySelector('.cart-drawer__slider-container');
  if(cartDrawerSlider) cartDrawerSlider.classList.add('hidden');
}

/**
 * Triggers FindMine product match for cart recommendations
 *
 * Attempts to get product recommendations from FindMine based on the provided product.
 * FindMine handles its own caching and fallback logic internally.
 *
 * @param {Object} params - Match parameters
 * @param {string} params.product_id - Product ID
 * @param {string} params.product_color_id - Color/variant identifier
 * @param {boolean} params.product_in_stock - Whether the product is in stock
 *
 * @global
 */
window.findMineCartMatch = ({ product_id, product_color_id, product_in_stock }) => {
  const matchData = {
    application: window.theme?.apps?.findmine?.cart_application_id,
    product_id,
    product_color_id,
    product_in_stock,
    return_pdp_item: true,
    event_handlers: {
      onFinish: () => {
        // Hide the cart drawer slider now that FindMine content is displayed
        hideCartDrawerSlider();
      }
    }
  };

  console.log(`FindMineCart.match attempting:`, matchData);
  window.FindMineCart?.match(matchData);
};

/**
 * Loads the FindMine external script
 *
 * Creates and injects the FindMine cart script tag into the page.
 * Returns a promise that resolves when the script is fully loaded and ready.
 *
 * @returns {Promise<void>} Resolves when FindMine script is loaded
 */
const loadFindMineScript = () => {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `//js.findmine.com/${window.theme?.apps?.findmine?.cart_application_id}.module.js`;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load FindMine script'));
    document.head.appendChild(script);
  });
};

/**
 * Handle cart state changes
 *
 * Updates FindMine recommendations when items are added, removed, or updated in the cart.
 * Only runs when the cart drawer is open to avoid unnecessary API calls.
 *
 * @listens cart-state:changed
 */
document.addEventListener('cart-state:changed', (event) => {
  // Only proceed if cart drawer is open
  const cartDrawer = document.querySelector('cart-drawer');
  if (!cartDrawer?.isOpen) {
    // console.log('FindMine: Cart drawer not open, skipping match');
    return;
  }

  // console.log('cart state changed', event.detail);
  const { state, changes } = event.detail;

  // Only trigger on item changes (not just total/price updates)
  const itemsChanged = changes && (changes.itemsAdded || changes.itemsRemoved || changes.itemsUpdated);
  if (!itemsChanged) return;

  // If cart is empty, clear FindMine content
  if (!state.items || state.items.length === 0) {
    const findmineContainer = document.querySelector('#findmine-cart-container');
    if (findmineContainer) {
      findmineContainer.innerHTML = '';
    }
    return;
  }

  // Get the first item from the cart state
  const firstItem = state.items[0];
  window.findMineCartMatch({
    product_id: firstItem.productId,
    product_color_id: firstItem.colorId,
    product_in_stock: firstItem.available || false,
  });
});

/**
 * Handle cart drawer opened event
 *
 * Triggers FindMine match when the cart drawer opens, ensuring recommendations
 * are displayed with the current cart contents. Uses the cart state provided
 * in the event detail for immediate access without additional queries.
 *
 * Loads the FindMine script on first drawer open and waits for it to initialize
 * before triggering the first match.
 *
 * @listens cart-drawer:opened
 */
document.addEventListener('cart-drawer:opened', async (event) => {
  const state = event.detail?.state;

  // If cart is empty, clear FindMine content and exit
  if (!state?.items || state.items.length === 0) {
    const findmineContainer = document.querySelector('#findmine-cart-container');
    if (findmineContainer) {
      findmineContainer.innerHTML = '';
    }
    return;
  }

  // Load script if not already present and wait for it to be ready
  if (!window.FindMineCart) {
    try {
      await loadFindMineScript();
      // console.log('FindMine cart script loaded and ready');
    } catch (error) {
      console.error('Failed to load FindMine script:', error);
      return;
    }
  }

  const firstItem = state.items[0];
  window.findMineCartMatch({
    product_id: firstItem.productId,
    product_color_id: firstItem.colorId,
    product_in_stock: firstItem.available || false,
  });
});
